import type {
  PerformanceDataPoint,
  PerformanceStats,
  DrawdownDataPoint,
  MonthlyReturn,
  PnLBucket,
} from '@/types/performance';

/**
 * Calculate comprehensive performance statistics from an array of data points.
 * Internally calls calculateDrawdown and findMaxDrawdown for drawdown fields.
 */
export function calculatePerformanceStats(
  data: PerformanceDataPoint[]
): PerformanceStats {
  const emptyStats: PerformanceStats = {
    totalReturn: 0,
    totalReturnPercent: 0,
    startValue: 0,
    endValue: 0,
    maxValue: 0,
    minValue: 0,
    avgDailyPnl: 0,
    maxDrawdown: 0,
    maxDrawdownStartDate: '',
    maxDrawdownEndDate: '',
    alpha: null,
  };

  if (data.length === 0) {
    return emptyStats;
  }

  const first = data[0]!;
  const last = data[data.length - 1]!;

  if (data.length === 1) {
    return {
      ...emptyStats,
      startValue: first.value,
      endValue: first.value,
      maxValue: first.value,
      minValue: first.value,
      avgDailyPnl: first.pnl,
      maxDrawdownStartDate: first.date,
      maxDrawdownEndDate: first.date,
    };
  }

  const startValue = first.value;
  const endValue = last.value;
  const totalReturn = endValue - startValue;
  const totalReturnPercent =
    startValue !== 0 ? (totalReturn / startValue) * 100 : 0;

  let maxValue = first.value;
  let minValue = first.value;
  let totalPnl = 0;

  for (const point of data) {
    if (point.value > maxValue) maxValue = point.value;
    if (point.value < minValue) minValue = point.value;
    totalPnl += point.pnl;
  }

  const avgDailyPnl = totalPnl / data.length;

  const drawdownData = calculateDrawdown(
    data.map((d) => ({ date: d.date, value: d.value }))
  );
  const maxDrawdownInfo = findMaxDrawdown(drawdownData);

  return {
    totalReturn,
    totalReturnPercent,
    startValue,
    endValue,
    maxValue,
    minValue,
    avgDailyPnl,
    maxDrawdown: maxDrawdownInfo.value,
    maxDrawdownStartDate: maxDrawdownInfo.startDate,
    maxDrawdownEndDate: maxDrawdownInfo.endDate,
    alpha: null,
  };
}

/**
 * Calculate drawdown at each data point.
 * Drawdown = (currentValue - runningPeak) / runningPeak * 100
 * Duration tracks days since the last peak.
 */
export function calculateDrawdown(
  data: Array<{ date: string; value: number }>
): DrawdownDataPoint[] {
  if (data.length === 0) {
    return [];
  }

  const first = data[0]!;
  const result: DrawdownDataPoint[] = [];
  let runningPeak = first.value;
  let daysSincePeak = 0;

  for (const point of data) {
    if (point.value >= runningPeak) {
      runningPeak = point.value;
      daysSincePeak = 0;
    } else {
      daysSincePeak++;
    }

    const drawdown =
      runningPeak !== 0
        ? ((point.value - runningPeak) / runningPeak) * 100
        : 0;

    result.push({
      date: point.date,
      drawdown,
      peakValue: runningPeak,
      duration: daysSincePeak,
    });
  }

  return result;
}

/**
 * Find the maximum (most negative) drawdown value and its date range.
 * The start date is the peak date before the trough, and end date is the trough date.
 */
export function findMaxDrawdown(drawdownData: DrawdownDataPoint[]): {
  value: number;
  startDate: string;
  endDate: string;
} {
  if (drawdownData.length === 0) {
    return { value: 0, startDate: '', endDate: '' };
  }

  const firstPoint = drawdownData[0]!;
  let maxDrawdownValue = 0;
  let maxDrawdownIndex = 0;

  for (let i = 0; i < drawdownData.length; i++) {
    const point = drawdownData[i]!;
    if (point.drawdown < maxDrawdownValue) {
      maxDrawdownValue = point.drawdown;
      maxDrawdownIndex = i;
    }
  }

  // If no drawdown occurred, return the first date for both
  if (maxDrawdownValue === 0) {
    return {
      value: 0,
      startDate: firstPoint.date,
      endDate: firstPoint.date,
    };
  }

  // Find the peak date (start of drawdown period)
  // Walk backwards from the max drawdown point to find where duration was 0
  let peakIndex = maxDrawdownIndex;
  for (let i = maxDrawdownIndex; i >= 0; i--) {
    const point = drawdownData[i]!;
    if (point.duration === 0) {
      peakIndex = i;
      break;
    }
  }

  const peakPoint = drawdownData[peakIndex]!;
  const troughPoint = drawdownData[maxDrawdownIndex]!;

  return {
    value: maxDrawdownValue,
    startDate: peakPoint.date,
    endDate: troughPoint.date,
  };
}

/**
 * Group data by month/year and calculate return for each month
 * based on first and last value in that month.
 */
export function calculateMonthlyReturns(
  data: PerformanceDataPoint[]
): MonthlyReturn[] {
  if (data.length === 0) {
    return [];
  }

  // Group data points by year-month
  const monthGroups = new Map<string, PerformanceDataPoint[]>();

  for (const point of data) {
    const date = new Date(point.date);
    const key = `${date.getFullYear()}-${date.getMonth() + 1}`;
    const group = monthGroups.get(key);
    if (group) {
      group.push(point);
    } else {
      monthGroups.set(key, [point]);
    }
  }

  const results: MonthlyReturn[] = [];

  for (const [key, points] of monthGroups) {
    const parts = key.split('-');
    const yearStr = parts[0] ?? '0';
    const monthStr = parts[1] ?? '0';
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);

    const firstPoint = points[0]!;
    const lastPoint = points[points.length - 1]!;
    const firstValue = firstPoint.value;
    const lastValue = lastPoint.value;
    const returnDollar = lastValue - firstValue;
    const returnPercent =
      firstValue !== 0 ? (returnDollar / firstValue) * 100 : 0;

    results.push({
      year,
      month,
      returnPercent,
      returnDollar,
    });
  }

  return results;
}

/**
 * Calculate arithmetic mean return for each month (1-12) across all years.
 * Returns an array of 12 values (index 0 = January, index 11 = December).
 */
export function calculateMonthlyAverages(
  monthlyReturns: MonthlyReturn[]
): number[] {
  const monthSums: number[] = new Array<number>(12).fill(0);
  const monthCounts: number[] = new Array<number>(12).fill(0);

  for (const mr of monthlyReturns) {
    const index = mr.month - 1; // month is 1-12, array is 0-indexed
    monthSums[index] = (monthSums[index] ?? 0) + mr.returnPercent;
    monthCounts[index] = (monthCounts[index] ?? 0) + 1;
  }

  return monthSums.map((sum, i) => {
    const count = monthCounts[i] ?? 0;
    return count > 0 ? sum / count : 0;
  });
}

/**
 * Create histogram buckets from PnL values.
 * Default 10 buckets. Each bucket is marked as profit or loss based on its midpoint.
 */
export function calculatePnLDistribution(
  pnlValues: number[],
  bucketCount: number = 10
): PnLBucket[] {
  if (pnlValues.length === 0 || bucketCount <= 0) {
    return [];
  }

  const min = Math.min(...pnlValues);
  const max = Math.max(...pnlValues);

  // If all values are the same, return a single bucket
  if (min === max) {
    return [
      {
        rangeStart: min,
        rangeEnd: max,
        count: pnlValues.length,
        isProfit: min > 0,
      },
    ];
  }

  const bucketWidth = (max - min) / bucketCount;
  const buckets: PnLBucket[] = [];

  for (let i = 0; i < bucketCount; i++) {
    const rangeStart = min + i * bucketWidth;
    const rangeEnd = min + (i + 1) * bucketWidth;
    const midpoint = (rangeStart + rangeEnd) / 2;

    buckets.push({
      rangeStart,
      rangeEnd,
      count: 0,
      isProfit: midpoint > 0,
    });
  }

  // Assign values to buckets
  for (const value of pnlValues) {
    let bucketIndex = Math.floor((value - min) / bucketWidth);
    // Clamp the last value into the final bucket
    if (bucketIndex >= bucketCount) {
      bucketIndex = bucketCount - 1;
    }
    const bucket = buckets[bucketIndex];
    if (bucket) {
      bucket.count++;
    }
  }

  return buckets;
}

/**
 * Calculate percentage of PnL values that are greater than 0.
 */
export function calculateWinRate(pnlValues: number[]): number {
  if (pnlValues.length === 0) {
    return 0;
  }

  const winners = pnlValues.filter((v) => v > 0).length;
  return (winners / pnlValues.length) * 100;
}

/**
 * Calculate arithmetic mean and median of PnL values.
 */
export function calculateMeanMedianPnl(pnlValues: number[]): {
  mean: number;
  median: number;
} {
  if (pnlValues.length === 0) {
    return { mean: 0, median: 0 };
  }

  const mean = pnlValues.reduce((sum, v) => sum + v, 0) / pnlValues.length;

  const sorted = [...pnlValues].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  let median: number;
  if (sorted.length % 2 === 0) {
    const lower = sorted[mid - 1] ?? 0;
    const upper = sorted[mid] ?? 0;
    median = (lower + upper) / 2;
  } else {
    median = sorted[mid] ?? 0;
  }

  return { mean, median };
}

/**
 * Normalize both portfolio and benchmark value series to start at 100.
 * Both arrays should have the same length (pre-aligned by caller).
 */
export function normalizeToBenchmark(
  portfolioValues: number[],
  benchmarkValues: number[]
): { normalizedPortfolio: number[]; normalizedBenchmark: number[] } {
  if (portfolioValues.length === 0 || benchmarkValues.length === 0) {
    return { normalizedPortfolio: [], normalizedBenchmark: [] };
  }

  const portfolioStart = portfolioValues[0] ?? 1;
  const benchmarkStart = benchmarkValues[0] ?? 1;

  const normalizedPortfolio = portfolioValues.map((v) =>
    portfolioStart !== 0 ? (v / portfolioStart) * 100 : 0
  );

  const normalizedBenchmark = benchmarkValues.map((v) =>
    benchmarkStart !== 0 ? (v / benchmarkStart) * 100 : 0
  );

  return { normalizedPortfolio, normalizedBenchmark };
}

/**
 * Calculate simple alpha: portfolio return minus benchmark return.
 */
export function calculateAlpha(
  portfolioReturnPercent: number,
  benchmarkReturnPercent: number
): number {
  return portfolioReturnPercent - benchmarkReturnPercent;
}
